import React from 'react';
import { createRoot } from 'react-dom/client';
import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { jsPDF } from 'jspdf';
import { toPng, getFontEmbedCSS } from 'html-to-image';
import type { CardTemplate } from './scoreCardTypes';
import { ScoreCodeCardItem } from './ScoreCodeCardItem';

// ─── PDF layout constants ─────────────────────────────────────────────────────
// Letter page: 612pt × 792pt  (8.5" × 11")
// Card size:   252pt × 180pt  (3.5" × 2.5") — standard business card
// Grid:        2 cols × 4 rows = 8 cards per page, no gaps between cards.
// Margins center the grid: 54pt (0.75") left/right, 36pt (0.5") top/bottom.

const PAGE_W   = 612;
const PAGE_H   = 792;
const COLS     = 2;
const ROWS     = 4;
const CARD_W   = 252;  // 3.5" × 72pt/in
const CARD_H   = 180;  // 2.5" × 72pt/in
const MARGIN_X = (PAGE_W - COLS * CARD_W) / 2;  // 54pt (0.75")
const MARGIN_Y = (PAGE_H - ROWS * CARD_H) / 2;  // 36pt (0.5")

const RENDER_W = CARD_W;

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface GeneratePDFOptions {
    registrations: Record<string, unknown>[];
    template: CardTemplate;
    viewEvent: Record<string, unknown> | null;
    roundValue: number;
}

export function useScoreCodeCardPDF() {
    const auth = useAuth();

    const generatePDF = useCallback(async (opts: GeneratePDFOptions) => {
        const { registrations, template, viewEvent, roundValue } = opts;

        const cards = registrations;
        if (cards.length === 0) return;

        // ── Resolve CDN image URLs to base64 through the server proxy ─────────
        // html-to-image needs all resources as data URIs or same-origin to avoid
        // canvas tainting. Azure Blob Storage may not have permissive CORS headers,
        // so route all CDN URLs through our proxy first.
        const resolvedTemplate = await resolveTemplateImages(
            template,
            process.env.REACT_APP_API_URL ?? '',
            auth.user?.access_token,
        );

        // ── Ensure Fredoka (and any other web fonts) are loaded ───────────────
        // The font is already declared in index.html via Google Fonts; this just
        // waits for the browser to finish downloading it.
        await document.fonts.ready;

        // ── Render cards off-screen and capture as PNGs ───────────────────────
        const pngDataUrls = await captureCardsAsPngs(resolvedTemplate, cards, roundValue, viewEvent);

        // ── Assemble PDF ──────────────────────────────────────────────────────
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });

        pngDataUrls.forEach((pngUrl, cardIndex) => {
            const posOnPage = cardIndex % (COLS * ROWS);
            if (posOnPage === 0 && cardIndex > 0) doc.addPage();

            const col = posOnPage % COLS;
            const row = Math.floor(posOnPage / COLS);
            const x   = MARGIN_X + col * CARD_W;
            const y   = MARGIN_Y + row * CARD_H;

            // Thin cut-guide border
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.rect(x, y, CARD_W, CARD_H, 'S');

            doc.addImage(pngUrl, 'PNG', x, y, CARD_W, CARD_H);
        });

        const eventName = (viewEvent as any)?.eventName ?? 'Event';
        doc.save(`${eventName.replace(/[^a-z0-9]/gi, '_')}_score_code_cards.pdf`);
    }, [auth]);

    return { generatePDF };
}

// ─── Resolve template image elements to base64 data URLs ──────────────────────
// Replaces every CDN URL in image elements with a proxied base64 data URL so
// that html-to-image can embed them without hitting CORS restrictions.

async function resolveTemplateImages(
    template: CardTemplate,
    apiBase: string,
    accessToken: string | undefined,
): Promise<CardTemplate> {
    const resolvedElements = await Promise.all(
        template.elements.map(async (el) => {
            if (el.type !== 'image' || !el.imageDataUrl) return el;
            if (el.imageDataUrl.startsWith('data:')) return el;

            try {
                const fetchUrl = `${apiBase}/image/proxyImage?url=${encodeURIComponent(el.imageDataUrl)}`;
                const headers: HeadersInit = accessToken
                    ? { Authorization: `Bearer ${accessToken}` }
                    : {};
                const res  = await fetch(fetchUrl, { headers });
                if (!res.ok) throw new Error(`Proxy ${res.status}`);
                const blob = await res.blob();
                return { ...el, imageDataUrl: await blobToDataUrl(blob) };
            } catch (err) {
                console.warn('Score card image fetch failed, keeping original URL:', el.imageDataUrl, err);
                return el;
            }
        }),
    );
    return { ...template, elements: resolvedElements };
}

// ─── Off-screen render + capture ─────────────────────────────────────────────
// Creates a hidden container in the document body, mounts one React root per
// card (so all renders and QR code useEffects run in parallel), waits for
// everything to settle, then captures each card element as a PNG via
// html-to-image. Cleans up the roots and the container when done.

async function captureCardsAsPngs(
    template: CardTemplate,
    cards: Record<string, unknown>[],
    roundValue: number,
    viewEvent: Record<string, unknown> | null,
): Promise<string[]> {
    const container = document.createElement('div');
    Object.assign(container.style, {
        position : 'fixed',
        top      : '0px',
        left     : '-9999px',
        display  : 'flex',
        flexDirection: 'column',
        pointerEvents: 'none',
        zIndex   : '-1',
    });
    document.body.appendChild(container);

    // One wrapper div per card, each gets its own React root.
    const wrappers: HTMLDivElement[] = cards.map(() => {
        const div = document.createElement('div');
        container.appendChild(div);
        return div;
    });

    const roots = wrappers.map((div, i) => {
        const root = createRoot(div);
        root.render(
            React.createElement(ScoreCodeCardItem, {
                template,
                registration: cards[i],
                roundValue,
                eventData: viewEvent ?? undefined,
                displayWidth: RENDER_W,
            }),
        );
        return root;
    });

    // Wait for React to flush + QR code useEffects to complete (qrcode.js
    // resolves in <50ms; 600ms gives a comfortable margin for slow devices).
    await new Promise<void>((resolve) => setTimeout(resolve, 600));

    // Pre-compute font embed CSS once from the first card so html-to-image
    // doesn't re-fetch Google Fonts for every single capture.
    const firstCardEl = wrappers[0].firstElementChild as HTMLElement;
    const fontEmbedCSS = await getFontEmbedCSS(firstCardEl);

    // pixelRatio = 300 DPI ÷ 72 pt/in ≈ 4.167 — produces a 300 PPI image in the PDF
    const PRINT_PIXEL_RATIO = 300 / 72;

    // Capture all cards in parallel, reusing the pre-fetched font CSS.
    const pngs = await Promise.all(
        wrappers.map((div) => {
            const cardEl = div.firstElementChild as HTMLElement;
            return toPng(cardEl, { pixelRatio: PRINT_PIXEL_RATIO, fontEmbedCSS });
        }),
    );

    roots.forEach((root) => root.unmount());
    document.body.removeChild(container);

    return pngs;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
